import { Button, CloseButton, Dialog, Field, Input, Portal, Stack } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import { FaPlus } from 'react-icons/fa';

import { ApiError, PillarCreate, PillarsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

export const PillarAddDialog = ({ slug, order }: { slug: string; order: number }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const queryClient = useQueryClient();
  const showToast = useCustomToast();

  const mutationPillar = useMutation({
    mutationFn: async ({ data }: { data: PillarCreate }) =>
      await PillarsService.createPillar({
        companySlug: slug,
        requestBody: data,
      }),
    onSuccess: async () => {
      showToast('Succès', 'Pilier ajouter avec succès', 'success');
      await queryClient.invalidateQueries({
        queryKey: ['csrPlans'],
      });
      setOpen(false);
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
  });

  const onSubmit = () => {
    mutationPillar.mutate({
      data: {
        name,
        order,
      },
    });
  };

  return (
    <Dialog.Root lazyMount initialFocusEl={() => ref.current} open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Dialog.Trigger asChild>
        <Button w="400px" variant="outline" borderStyle={'dashed'} borderColor={'gray.300'}>
          Ajouter un pilier <FaPlus size={16} />
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Ajouter un pilier</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body pb="4">
              <Stack gap="4">
                <Field.Root required>
                  <Field.Label>
                    Nom du pilier <Field.RequiredIndicator />
                  </Field.Label>
                  <Input placeholder="Nom du pilier" value={name} onChange={(e) => setName(e.target.value)} />
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button onClick={onSubmit} bg={'primary'} disabled={name === ''}>
                {t('ui.save')}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
